import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { formatNumber } from '../../../../shared/formatNumber';
import locales from '../../../../shared/locales';
import { colorsChart } from '../../../../shared/colorsCharts';
import * as actions from '../../../../store/actions/index';
import LineChart from '../../../Charts/CustomLineChart';

const LineRentabilidadEvolutivo = (props) => {
  const { ejecucionDePagina, onLoadChart } = props;
  const { loading, estaCargado, fechaPeriodo, gastosColums, itemData } = props;
  const { costos = [], ganancias = [], utilidades = [], ventas = [] } = itemData;

  const [labelMode, setLabelMode] = useState('classic'); // 'stacked' | 'classic' | 'none'
  const wrapperRef = useRef(null);


  const applyActive = (group, mode) => {
    if (!group) return;
    group.querySelectorAll('button').forEach((btn) => {
      const isActive = btn.dataset.mode === mode;
      btn.dataset.active = isActive ? '1' : '0';
      btn.style.background = isActive ? '#e7f1ff' : '#fff';
      btn.style.borderColor = isActive ? '#86b7ff' : '#d9d9d9';
      btn.style.fontWeight = isActive ? '700' : '400';
    });
  };

  // Inserta label + botones en la toolbar de Apex y evita desborde
  useLayoutEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;

    const tryInject = () => {
      const toolbar = root.querySelector('.apexcharts-toolbar');
      if (!toolbar) return;

      // Evitar desborde horizontal
      Object.assign(toolbar.style, {
        position: 'absolute',
        left: '8px',
        right: '8px',
        top: '0',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '6px',
        width: 'auto',
        maxWidth: 'unset',
        transform: 'none',
        overflow: 'visible'
      });

      // Eliminar grupos duplicados si los hubiera
      const groups = toolbar.querySelectorAll('.apex-labelmodes-group');
      if (groups.length > 1) groups.forEach((g, idx) => { if (idx > 0) g.remove(); });

      // Crear o reutilizar grupo
      let group = toolbar.querySelector('.apex-labelmodes-group');
      if (!group) {
        group = document.createElement('div');
        group.className = 'apex-labelmodes-group';
        Object.assign(group.style, {
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          marginRight: '6px',
          flex: '0 1 auto',
          whiteSpace: 'nowrap'
        });

        // Label fijo
        const labelEl = document.createElement('span');
        labelEl.textContent = 'Mostrar etiquetas en modo:';
        Object.assign(labelEl.style, {
          fontSize: '11px',
          color: '#666',
          marginRight: '6px',
          userSelect: 'none'
        });
        group.appendChild(labelEl);

        const makeBtn = (text, mode) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.textContent = text;
          btn.dataset.mode = mode;
          btn.dataset.active = '0';
          Object.assign(btn.style, {
            height: '22px',
            lineHeight: '20px',
            padding: '0 8px',
            fontSize: '11px',
            background: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '3px',
            boxShadow: '0 1px 0 rgba(0,0,0,.03)',
            cursor: 'pointer',
            userSelect: 'none',
            fontWeight: '400'
          });
          btn.onmouseenter = () =>
            (btn.style.background = btn.dataset.active === '1' ? '#e7f1ff' : '#fafafa');
          btn.onmouseleave = () =>
            (btn.style.background = btn.dataset.active === '1' ? '#e7f1ff' : '#fff');
          btn.onclick = () => {
            setLabelMode(mode);     // React actualiza anotaciones
            applyActive(group, mode); // feedback inmediato
          };
          return btn;
        };

        // Orden: Clásico (default), Apiladas, Ocultas
        group.appendChild(makeBtn('Clásico', 'classic'));
        group.appendChild(makeBtn('Apiladas', 'stacked'));
        group.appendChild(makeBtn('Ocultas',  'none'));

        toolbar.insertBefore(group, toolbar.firstChild);
      } else {
        // si el grupo existe pero no tiene label, lo añadimos
        if (!group.querySelector('span')) {
          const labelEl = document.createElement('span');
          labelEl.textContent = 'Mostrar etiquetas en modo:';
          Object.assign(labelEl.style, {
            fontSize: '11px',
            color: '#666',
            marginRight: '6px',
            userSelect: 'none'
          });
          group.insertBefore(labelEl, group.firstChild);
        }
      }

      // reflejar el modo actual
      applyActive(group, labelMode);
    };

    // Primer intento + observar reconstrucciones de Apex
    tryInject();
    const mo = new MutationObserver(tryInject);
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [labelMode]);

  // Sincronía extra por cualquier cambio
  useEffect(() => {
    const root = wrapperRef.current;
    applyActive(root?.querySelector('.apexcharts-toolbar .apex-labelmodes-group'), labelMode);
  }, [labelMode]);

  useEffect(() => {
    if (!estaCargado && ejecucionDePagina) {
      onLoadChart(fechaPeriodo.mes, fechaPeriodo.anio);
    }
  }, [ejecucionDePagina, estaCargado, fechaPeriodo, onLoadChart]);

  const series = () => {
    let items = [
      { name: 'Ventas',   data: ventas.map(v => v.monto) },
      { name: 'Costos',   data: costos.map(c => c.monto) },
      { name: 'Utilidad', data: utilidades.map(u => u.monto) }
    ];
    if (gastosColums) {
      gastosColums.forEach((col) => {
        const montos = itemData[col.field];
        items.push({ name: col.nombre, data: montos ? montos.map(m => m.monto) : [] });
      });
    }
    items.push({ name: 'Resultado', data: ganancias.map(g => g.monto) });
    return items;
  };

  const formatMesesToStrings = () =>
    utilidades.map(c => `${locales[1].options.months[c.mes - 1]}/${c.anio}`);

  // ---------- Etiquetas ----------
  const getFmt = (n) => `${sessionStorage.getItem('monedaSigno')} ${formatNumber(n)}`;

  // Modo apiladas
  const buildStackedPointAnnotations = (seriesArr, categories) => {
    const points = [];
    const nCategories = categories.length;
    const allValues = seriesArr.flatMap(s => (s.data || [])).filter(v => v != null);
    const minY = allValues.length ? Math.min(...allValues) : 0;
    const maxY = allValues.length ? Math.max(...allValues) : 1;
    const spanY = Math.max(1, maxY - minY);

    const chartHeightPx = 300;
    const plotHeightPx = Math.max(1, Math.round(chartHeightPx * 0.82));
    const pxToValue = spanY / plotHeightPx;

    const LABEL_FONT_PX = 11, PADDING_V_PX = 4, SAFETY_PX = 6;
    const GAP_PX = Math.max(35, LABEL_FONT_PX + PADDING_V_PX + SAFETY_PX);
    const GAP_VAL = GAP_PX * pxToValue;
    const STACK_OFFSET = 0.3 * GAP_VAL;
    const X_SPREAD_PX = 10;

    for (let i = 0; i < nCategories; i++) {
      const pack = [];
      seriesArr.forEach((s, sIdx) => {
        const val = (s.data || [])[i];
        if (val == null) return;
        pack.push({ sIdx, val });
      });
      if (!pack.length) continue;

      pack.sort((a, b) => b.val - a.val);
      const topVal = pack[0].val;

      pack.forEach((item, k) => {
        const color = colorsChart[item.sIdx % colorsChart.length];
        const yPos = topVal - STACK_OFFSET - k * GAP_VAL;
        const offsetX = (k % 2 === 0 ? -X_SPREAD_PX : X_SPREAD_PX);

        points.push({
          x: formatMesesToStrings()[i],
          y: yPos,
          marker: { size: 0 },
          label: {
            text: getFmt(item.val),
            offsetX,
            borderColor: color,
            style: {
              background: color, color: '#fff',
              fontSize: `${LABEL_FONT_PX}px`, fontWeight: 600,
              borderRadius: '3px',
              padding: { left: 4, right: 4, top: 2, bottom: 2 }
            }
          }
        });
      });
    }
    return { points };
  };

  // Modo clásico
  const buildClassicPointAnnotations = (seriesArr, categories) => {
    const points = [];
    const LABEL_FONT_PX = 11;

    seriesArr.forEach((s, sIdx) => {
      const color = colorsChart[sIdx % colorsChart.length];
      (s.data || []).forEach((val, i) => {
        if (val == null) return;
        const offsetX = (i % 2 === 0 ? -8 : 8), offsetY = -6;
        points.push({
          x: categories[i],
          y: val,
          marker: { size: 0 },
          label: {
            text: getFmt(val),
            offsetX, offsetY,
            borderColor: color,
            style: {
              background: color, color: '#fff',
              fontSize: `${LABEL_FONT_PX}px`, fontWeight: 600,
              borderRadius: '3px',
              padding: { left: 4, right: 4, top: 2, bottom: 2 }
            }
          }
        });
      });
    });
    return { points };
  };

  const s = series();
  const categorias = formatMesesToStrings();

  const annotations =
    labelMode === 'stacked'
      ? buildStackedPointAnnotations(s, categorias)
      : labelMode === 'classic'
      ? buildClassicPointAnnotations(s, categorias)
      : { points: [] };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <LineChart
        title="Resultado evolutivo"
        series={s}
        loading={loading}
        chartHeight="300px"
        width="100%"
        chartWidth="99%"
        chartDropShadow={{ enabled: false, color: '#000', top: 18, left: 7, blur: 10, opacity: 0.2 }}
        toolbarShow={true}
        zoomType="xy"
        dataLabelsEnabled={false}
        formatDataLabels
        strokeCurve="smooth"
        strokeWidth={5}
        fillType="solid"
        gridBorderColor="#e7e7e7"
        gridRow={{ colors: ['#f3f3f3', 'transparent'], opacity: 0.5 }}
        markersSize={4}
        xaxisCategories={categorias}
        xaxisType="category"
        yaxisLabels={{
          show: true,
          formatter: (value) => `${sessionStorage.getItem('monedaSigno')} ${formatNumber(value)}`,
          align: 'left',
          offsetX: -16
        }}
        legendFontSize="14px"
        legendOffsetY={-5}
        tooltipEnabled
        legendPosition="bottom"
        legendHorizontalAlign="center"
        legendShowForSingleSeries={true}
        legendShowForNullSeries={true}
        legendShowForZeroSeries={true}
        annotations={annotations}
        onFiltersClick={() => props.onShowFiltersModal()}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  itemData: state.integradores.lineRentabilidadEvolutivo,
  fechaPeriodo: state.integradores.fechaPeriodoRentabilidad,
  loading: state.integradores.lineRentabilidadEvolutivo.loading,
  progressLoading: state.integradores.lineRentabilidadEvolutivo.progress,
  estaCargado: state.integradores.lineRentabilidadEvolutivo.estaCargado,
  gastosColums: state.integradores.lineRentabilidadEvolutivo.columns
});

const mapDispatchToProps = (dispatch) => ({
  onLoadChart: (mes, anio) => dispatch(actions.loadLineRentabilidadEvolutivo(mes, anio)),
  onShowFiltersModal: () =>
    dispatch(actions.showListFiltersModal(true, 'Resultado evolutivo', [
      'gasto', 'venta', 'categoriaItemsDeCompra'
    ]))
});

export default connect(mapStateToProps, mapDispatchToProps)(LineRentabilidadEvolutivo);
